<template>
  <b-modal
    modal-class="component-modal-product"
    id="stockAdjustmentModal"
    ref="stockAdjustmentModal"
    size="xl"
    :title="modalTitle"
    no-close-on-backdrop
    @close="close"
  >
    <b-form id="stockAdjustmentTable" @submit.prevent="saveStockAdjustment">
      <b-form-group
        label-cols="3"
        label="รูปแบบการปรับสต๊อก:"
        label-align="right"
        class="align-label"
      >
        <b-form-radio-group v-model="stockType">
          <b-form-radio :value="0">ปรับปรุงทั่วไป</b-form-radio>
          <b-form-radio :value="1">ส่งคืนสินค้า</b-form-radio>
          <b-form-radio :value="2">ตรวจนับสินค้าทั้งหมด</b-form-radio>
        </b-form-radio-group>
        <small
          v-if="submitAttempted && $v.stockType.$error"
          class="text-danger d-block mt-1"
        >
          กรุณาเลือกรูปแบบการปรับสต๊อก
        </small>
      </b-form-group>
      <template
        v-if="
          this.stockType === 0 || this.stockType === 1 || this.stockType === 2
        "
      >
        <b-form-group label-cols="3" label="เหตุผล:" label-align="right">
          <b-form-input
            class="required"
            id="reasonStock"
            v-model="reasonStock"
            :class="{
              'is-invalid': $v.reasonStock.$error,
            }"
            maxlength="500"
            placeholder="กรอกรายละเอียด"
          ></b-form-input>
          <template v-slot:description> </template>
        </b-form-group>
      </template>

      <template v-if="stockType === 0">
        <b-col class="layout-table">
          <b-table
            id="GeneralAdjustmentStockTable"
            ref="GeneralAdjustmentStockTable"
            hover
            small
            sort-icon-left
            head-variant="light"
            :items="generalAdjustmentStock.productAdjustmentItems"
            :fields="generalAdjustmentFields"
          >
            <template v-slot:cell(index)="row">
              <div class="d-flex align-items-center justify-content-center">
                {{ row.index + 1 }}.
              </div>
            </template>

            <template v-slot:cell(productNoAndName)="row">
              <multiselect
                class="col p-0 required"
                v-model="row.item.productNoAndName"
                :options="getProductOptions(row.index)"
                :multiple="false"
                placeholder="เลือกสินค้า"
                @input="onProductNoAndNameChange(row)"
                track-by="id"
                :custom-label="item => item.name || ''"
                selectLabel="เลือกสินค้า"
                selectedLabel="เลือกแล้ว"
                deselectLabel="คลิกเพื่อลบออก"
                openDirection="below"
                :class="{
                  'is-invalid':
                    $v.generalAdjustmentStock.productAdjustmentItems.$each[
                      row.index
                    ] &&
                    $v.generalAdjustmentStock.productAdjustmentItems.$each[
                      row.index
                    ].productNoAndName.$error,
                }"
              >
                <span slot="noResult">ไม่พบรายการ แนะนำเปลี่ยนคำค้นหา</span>
              </multiselect>
            </template>

            <template v-slot:cell(adjustmentAmount)="row">
              <InputNumberWithButtonField
                :adjustStock="row.item.adjustmentAmount"
                :onChange="value => onChangeAdjustStock(row, value)"
                :disabled="!row.item.productNoAndName"
                :invalid="
                  submitAttempted &&
                  $v.generalAdjustmentStock.productAdjustmentItems.$each[
                    row.index
                  ] &&
                  $v.generalAdjustmentStock.productAdjustmentItems.$each[
                    row.index
                  ].adjustmentAmount.$error
                "
              />
            </template>

            <template v-slot:cell(currentStockToAfterAdjustment)="row">
              <PreAndPostStockField
                :currentStock="convertUnitStock(row)"
                :adjustStock="row.item.adjustmentAmount"
                :onChange="value => onChangeAdjustStock(row, value)"
                :disabled="!row.item.productNoAndName"
                :invalid="
                  submitAttempted && isCurrentStockToAfterAdjustmentInvalid(row)
                "
              />
            </template>

            <template v-slot:cell(productUnit)="row">
              <multiselect
                class="col p-0 required multiselect-unit"
                v-model="row.item.productUnit"
                :options="buildUomOptions(row.item.productNoAndName)"
                :multiple="false"
                :disabled="!row.item.productNoAndName"
                @input="onProductUnitChange(row)"
                placeholder="เลือกหน่วยสินค้า"
                track-by="id"
                :custom-label="customLabel"
                selectLabel="เลือกหน่วยสินค้า"
                selectedLabel="เลือกแล้ว"
                :allow-empty="false"
                :clear-on-select="false"
                :close-on-select="true"
                openDirection="below"
                deselectLabel=""
                :class="{
                  'is-invalid':
                    $v.generalAdjustmentStock.productAdjustmentItems.$each[
                      row.index
                    ] &&
                    $v.generalAdjustmentStock.productAdjustmentItems.$each[
                      row.index
                    ].productUnit.$error,
                }"
              >
                <span slot="noResult">ไม่พบรายการ แนะนำเปลี่ยนคำค้นหา</span>
              </multiselect>
            </template>

            <template v-slot:cell(buttons)="row">
              <button
                :disabled="
                  row.index === 0 &&
                  generalAdjustmentStock.productAdjustmentItems.length === 1
                "
                @click="deleteAdjustmentItem(row.item, row.index)"
                class="delete-btn"
              >
                <i class="fas fa-trash-alt"></i>
              </button>
            </template>

            <template v-slot:bottom-row>
              <td colspan="6" class="bottom-row-cell">
                <b-button
                  type="button"
                  variant="primary"
                  @click="addAdjustmentItem"
                  class="bottom-add-row"
                >
                  <i class="fas fa-plus"></i> เพิ่มรายการสินค้า
                </b-button>
              </td>
            </template>
          </b-table>
        </b-col>
      </template>

      <template v-if="stockType === 1">
        <b-form-group
          label-cols="3"
          label="รหัสใบรับสินค้าอ้างอิง:"
          label-align="right"
        >
          <multiselect
            class="col p-0 required"
            :class="{
              'is-invalid': $v.returnProductAdjustmentStock.GRNo.$error,
            }"
            v-model="returnProductAdjustmentStock.GRNo"
            @input="onGRNoSelect(returnProductAdjustmentStock.GRNo)"
            :options="GROptions"
            :multiple="false"
            placeholder="เลือกใบรับสินค้า (กรอกรหัสใบรับสินค้าเพื่อค้นหา)"
            track-by="id"
            :custom-label="customLabel"
            selectLabel="เลือกใบรับสินค้า"
            selectedLabel="เลือกแล้ว"
            deselectLabel="คลิกเพื่อลบออก"
            openDirection="below"
          >
            <span slot="noResult">ไม่พบรายการ แนะนำเปลี่ยนคำค้นหา</span>
          </multiselect>
        </b-form-group>
        <template v-if="returnProductAdjustmentStock.GRNo">
          <b-col class="layout-table">
            <b-table
              id="ReturnProductAdjustmentStockTable"
              ref="ReturnProductAdjustmentStockTable"
              hover
              small
              sort-icon-left
              head-variant="light"
              :items="returnProductAdjustmentStock.productAdjustmentItems"
              :fields="returnProductAdjustmentFields"
            >
              <template v-slot:cell(index)="row">
                <div class="d-flex align-items-center justify-content-center">
                  {{ row.index + 1 }}.
                </div>
              </template>

              <template v-slot:cell(adjustmentAmount)="row">
                <InputNumberWithButtonField
                  :adjustStock="row.item.adjustmentAmount"
                  :onChange="value => onChangeAdjustReturnStock(row, value)"
                  :max="0"
                  :min="-Number(row.item.productQtyOnGR || 0)"
                />
              </template>

              <template v-slot:cell(currentStockToAfterAdjustment)="row">
                <PreAndPostStockField
                  :currentStock="convertUnitStock(row)"
                  :adjustStock="row.item.adjustmentAmount"
                  :onChange="value => onChangeAdjustReturnStock(row, value)"
                  :max="Number(row.item.productAvailableStock || 0)"
                  :min="
                    Number(row.item.productAvailableStock || 0) -
                    Number(row.item.productQtyOnGR || 0)
                  "
                />
              </template>
            </b-table>
          </b-col>
        </template>
      </template>

      <template v-if="stockType === 2">
        <b-col class="layout-table">
          <b-table
            id="PhysicalProductAdjustmentStockTable"
            ref="PhysicalProductAdjustmentStockTable"
            hover
            small
            sort-icon-left
            head-variant="light"
            :items="physicalProductAdjustmentStock.productAdjustmentItems"
            :fields="physicalProductAdjustmentFields"
          >
            <template v-slot:cell(index)="row">
              <div class="d-flex align-items-center justify-content-center">
                {{ row.index + 1 }}.
              </div>
            </template>

            <template v-slot:cell(adjustmentAmount)="row">
              <InputNumberWithButtonField
                :adjustStock="row.item.adjustmentAmount"
                :onChange="value => onChangeAdjustPhysicalStock(row, value)"
              />
            </template>

            <template v-slot:cell(currentStockToAfterAdjustment)="row">
              <PreAndPostStockField
                :currentStock="convertUnitStock(row)"
                :adjustStock="row.item.adjustmentAmount"
                :onChange="value => onChangeAdjustPhysicalStock(row, value)"
              />
            </template>
          </b-table>
        </b-col>
      </template>
    </b-form>

    <template v-slot:modal-footer>
      <b-button type="submit" form="stockAdjustmentTable" variant="primary">
        ยืนยันสต๊อก
      </b-button>
    </template>
    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </b-modal>
</template>

<script>
import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";

import "@/theme/modal/ProductAndStock/Product.scss";
import { maxLength, required } from "vuelidate/lib/validators";
import BigNumber from "bignumber.js";
import PreAndPostStockField from "../../table/stock/fields/PreAndPostStockField.vue";
import InputNumberWithButtonField from "@/components/table/stock/fields/InputNumberWithButtonField.vue";
import { requiredIf } from "vuelidate/lib/validators";
import { mapGetters, mapActions } from "vuex";

export default {
  name: "StockAdjustmentModal",
  components: {
    Dialog,
    Loading,
    InputNumberWithButtonField,
    PreAndPostStockField,
  },
  data() {
    return {
      isLoading: false,
      submitAttempted: false,
      modalTitle: "ปรับปรุงสต๊อก",
      productList: [],
      unitList: [],
      allGRWithProductList: [],
      GRList: [],
      stockType: "",
      conversionUnitRate: [],
      reasonStock: "",
      generalAdjustmentStock: {
        productAdjustmentItems: [
          {
            productNoAndName: "",
            adjustmentAmount: 0,
            productUnit: null,
          },
        ],
      },
      returnProductAdjustmentStock: {
        GRNo: "",
        productAdjustmentItems: [],
      },
      physicalProductAdjustmentStock: {
        productAdjustmentItems: [],
      },
      generalAdjustmentFields: [
        {
          key: "index",
          label: "",
          class: "text-center",
          thStyle: { width: "5%" },
          tdStyle: { width: "5%" },
        },
        {
          key: "productNoAndName",
          label: "รหัสสินค้า - ชื่อสินค้า",
          thStyle: { width: "35%" },
          tdStyle: { width: "35%" },
        },
        {
          key: "adjustmentAmount",
          label: "จำนวนที่ปรับปรุง",
          thStyle: { width: "15%" },
          tdStyle: { width: "15%" },
        },
        {
          key: "currentStockToAfterAdjustment",
          label: "สต๊อก (ปัจจุบัน→หลังปรับปรุง)",
          thStyle: { width: "20%" },
          tdStyle: { width: "20%" },
        },
        {
          key: "productUnit",
          label: "หน่วยสินค้า",
          thStyle: { width: "20%" },
          tdStyle: { width: "20%" },
        },
        {
          key: "buttons",
          label: "",
          class: "text-center",
          thStyle: { width: "5%" },
          tdStyle: { width: "5%" },
        },
      ],
      returnProductAdjustmentFields: [
        {
          key: "index",
          label: "",
          class: "text-center",
          thStyle: { width: "4%" },
          tdStyle: { width: "4%" },
        },
        {
          key: "productNoAndName",
          label: "รหัสสินค้า - ชื่อสินค้า*",
          thStyle: { width: "34%" },
          tdStyle: { width: "34%" },
        },
        {
          key: "productQtyOnGR",
          label: "จำนวนจากใบรับสินค้า",
          thStyle: { width: "10%" },
          tdStyle: { width: "10%" },
        },
        {
          key: "adjustmentAmount",
          label: "จำนวนที่ปรับปรุง",
          thStyle: { width: "17%" },
          tdStyle: { width: "17%" },
        },
        {
          key: "currentStockToAfterAdjustment",
          label: "สต๊อก (ปัจจุบัน→หลังปรับปรุง)",
          thStyle: { width: "21%" },
          tdStyle: { width: "21%" },
        },
        {
          key: "productUnit",
          label: "หน่วยสินค้า",
          thStyle: { width: "14%" },
          tdStyle: { width: "14%" },
          tdClass: "text-wrap-cell",
        },
      ],
      physicalProductAdjustmentFields: [
        {
          key: "index",
          label: "",
          class: "text-center",
          thStyle: { width: "4%" },
          tdStyle: { width: "4%" },
        },
        {
          key: "productNoAndName",
          label: "รหัสสินค้า - ชื่อสินค้า",
          thStyle: { width: "34%" },
          tdStyle: { width: "34%" },
        },
        {
          key: "adjustmentAmount",
          label: "จำนวนที่ปรับปรุง",
          thStyle: { width: "17%" },
          tdStyle: { width: "17%" },
        },
        {
          key: "currentStockToAfterAdjustment",
          label: "สต๊อก (ปัจจุบัน→หลังปรับปรุง)",
          thStyle: { width: "21%" },
          tdStyle: { width: "21%" },
        },
        {
          key: "productUnit",
          label: "หน่วยสินค้า",
          thStyle: { width: "14%" },
          tdStyle: { width: "14%" },
          tdClass: "text-wrap-cell",
        },
      ],
    };
  },
  validations: {
    stockType: {
      required(value) {
        return value !== "" && value !== null && value !== undefined;
      },
    },
    reasonStock: {
      required,
      maxLength: maxLength(500),
    },
    generalAdjustmentStock: {
      productAdjustmentItems: {
        $each: {
          productNoAndName: {
            required(value) {
              return !!(value && value.id);
            },
          },
          adjustmentAmount: {
            required(value, row) {
              if (!row || !row.productNoAndName || !row.productNoAndName.id)
                return true;
              return value != null && Number(value) !== 0;
            },
          },
          currentStockToAfterAdjustment: {
            required(value, row) {
              if (!row || !row.productNoAndName || !row.productNoAndName.id)
                return true;

              const after = Number(value);
              const current = Number(row.productAvailableStock) || 0;
              return after !== current;
            },
          },
          productUnit: {
            required: requiredIf(function (row) {
              return !!(row && row.productNoAndName && row.productNoAndName.id);
            }),
          },
        },
      },
    },
    returnProductAdjustmentStock: {
      GRNo: {
        required,
        maxLength: maxLength(20),
      },
    },
  },
  computed: {
    unitOptions() {
      return (this.unitList || []).map(unit => ({
        id: unit.unitId,
        name: unit.unitNameTh,
      }));
    },
    productOptions() {
      return (this.productList || []).map(product => ({
        id: product.productId,
        name: `${product.productCode} - ${product.productNameMain}`,
      }));
    },
    GROptions() {
      return this.allGRWithProductList.map(item => ({
        id: item.GRNo,
        name: item.GRNo,
      }));
    },
    ...mapGetters({
      getProductList: "moduleProductInventory/getProductList",
    }),
  },
  methods: {
    ...mapActions({
      fetchProductList: "moduleProductInventory/fetchProductList",
    }),
    onChangeAdjustStock(row, value) {
      const num = Number(value);

      this.$set(row.item, "adjustmentAmount", num);
      const current = Number(row.item.productAvailableStock) || 0;

      const afterAdjustment = new BigNumber(current).plus(num).toFixed(2);
      this.$set(row.item, "currentStockToAfterAdjustment", afterAdjustment);

      const rowV =
        this.$v.generalAdjustmentStock?.productAdjustmentItems?.$each?.[
          row.index
        ];
      if (rowV?.adjustmentAmount?.$reset) rowV.adjustmentAmount.$reset();
      if (rowV?.currentStockToAfterAdjustment?.$reset)
        rowV.currentStockToAfterAdjustment.$reset();
    },
    onChangeAdjustReturnStock(row, value) {
      const num = Number(value);

      this.$set(row.item, "adjustmentAmount", num);
      const current = Number(row.item.productAvailableStock) || 0;

      const afterAdjustment = new BigNumber(current).plus(num).toFixed(2);
      this.$set(row.item, "currentStockToAfterAdjustment", afterAdjustment);

      const rowReturn =
        this.$v.returnProductAdjustmentStock?.productAdjustmentItems?.$each?.[
          row.index
        ];
      if (rowReturn?.adjustmentAmount?.$reset)
        rowReturn.adjustmentAmount.$reset();
      if (rowReturn?.currentStockToAfterAdjustment?.$reset)
        rowReturn.currentStockToAfterAdjustment.$reset();
    },
    onChangeAdjustPhysicalStock(row, value) {
      const num = Number(value);

      this.$set(row.item, "adjustmentAmount", num);
      const current = Number(row.item.productAvailableStock) || 0;

      const afterAdjustment = new BigNumber(current).plus(num).toFixed(2);
      this.$set(row.item, "currentStockToAfterAdjustment", afterAdjustment);
    },
    async show(data) {
      const { productList, unitList, allGRWithProductList } = data;
      this.unitList = unitList;
      this.productList = productList;
      this.allGRWithProductList = allGRWithProductList;
      this.getPhysicalProductAdjustmentStock();

      this.$bvModal.show("stockAdjustmentModal");
    },
    close() {
      this.$v.$reset();
      this.setDefaultValue();
      this.$bvModal.hide("stockAdjustmentModal");
    },
    reloadProductList() {
      this.fetchProductList({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      });
    },
    setDefaultValue() {
      this.submitAttempted = false;
      this.stockType = "";
      this.reasonStock = "";
      (this.generalAdjustmentStock = {
        productAdjustmentItems: [
          {
            productNoAndName: "",
            adjustmentAmount: 0,
            productUnit: null,
            currentStockToAfterAdjustment: null,
          },
        ],
      }),
        (this.returnProductAdjustmentStock = {
          GRNo: "",
          productAdjustmentItems: [],
        });
    },
    getAdjustmentProductsForSubmit() {
      const type = this.stockType;
      if (type === 0) {
        return this.generalAdjustmentStock.productAdjustmentItems.map(item => ({
          productId: item.productNoAndName.id,
          qtyUom: item.adjustmentAmount,
          uomId: item.productUnit.id,
        }));
      }
      const items =
        type === 1
          ? this.returnProductAdjustmentStock.productAdjustmentItems
          : this.physicalProductAdjustmentStock.productAdjustmentItems;
      return items.map(item => ({
        productId: item.productId,
        qtyUom: item.adjustmentAmount,
        uomId: item.productUnitId,
      }));
    },
    saveStockAdjustment() {
      this.submitAttempted = true;
      this.$v.$touch();

      const stockTypeInvalid = this.$v.stockType.$invalid;
      const reasonStockInvalid = this.$v.reasonStock.$invalid;
      const activeFormInvalid =
        this.stockType === 0
          ? this.$v.generalAdjustmentStock.$invalid
          : this.stockType === 1
          ? this.$v.returnProductAdjustmentStock.$invalid
          : false;

      if (stockTypeInvalid || reasonStockInvalid || activeFormInvalid) {
        return;
      }

      this.isLoading = true;

      const STOCK_TYPE_MAP = {
        0: "GENERAL",
        1: "RETURN",
        2: "PHYSICAL_COUNT",
      };

      const adjustStockData = {
        type: STOCK_TYPE_MAP[this.stockType],
        reason: this.reasonStock,
        ...(this.stockType === 1 && {
          refDocNo: this.returnProductAdjustmentStock.GRNo.name,
        }),
        adjustmentProducts: this.getAdjustmentProductsForSubmit(),
      };

      this.$store
        .dispatch("moduleProductInventory/createStockAdjustment", {
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          adjustStockData: adjustStockData,
        })
        .then(res => {
          if (res.status == 201) {
            this.reloadProductList();
            this.close();
            this.$parent.showDialogToast("success", "ปรับปรุงสต๊อกสำเร็จ");
          }
        })
        .catch(err => {
          const errorMessage = err.response?.data?.errorMessage;

          const errorMsg =
            typeof errorMessage === "string"
              ? errorMessage
              : "ปรับปรุงสต๊อกไม่สำเร็จ กรุณาลองใหม่";

          this.$parent.showDialogToast("danger", errorMsg);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    customLabel(item) {
      return item?.name || "";
    },
    buildUomOptions(productNoAndName) {
      const uoms = [];

      if (!productNoAndName) {
        return this.unitOptions;
      }

      const product = this.productList.find(
        p => p.productId === productNoAndName.id
      );

      if (!product) return uoms;

      if (product.productUnit) {
        uoms.push({
          id: product.productUnit.id,
          name: product.productUnit.description,
        });
      }

      if (product.productConversionUnitItems?.length) {
        product.productConversionUnitItems.forEach(item => {
          uoms.push({
            id: item.uom.id,
            name: item.uom.description,
          });
        });
      }

      const unique = uoms.filter(
        (uom, index, self) => index === self.findIndex(t => t.id === uom.id)
      );

      return unique;
    },
    onProductNoAndNameChange(row) {
      row.item.productUnit = null;

      if (row.item.productNoAndName?.id) {
        const product = this.productList.find(
          p => p.productId === row.item.productNoAndName.id
        );

        row.item.productUnit = product.productUnit
          ? {
              id: product.productUnit.id,
              name: product.productUnit.description,
            }
          : null;

        this.conversionUnitRate.push({
          id: product.productUnit.id,
          rate: 1,
        });

        if (product.productConversionUnitItems?.length) {
          product.productConversionUnitItems.forEach(item => {
            this.conversionUnitRate.push({
              id: item.uom.id,
              rate: 1 / item.qtyPerUnitOfMeasure,
            });
          });
        }

        const available = product?.productAvailableStock ?? 0;
        row.item.productAvailableStock = available;
        this.$set(row.item, "adjustmentAmount", 0);
        this.$set(row.item, "currentStockToAfterAdjustment", available);
      } else {
        row.item.productAvailableStock = undefined;

        this.$set(row.item, "adjustmentAmount", 0);
        row.item.currentStockToAfterAdjustment = undefined;
      }

      const rowValidation =
        this.$v.generalAdjustmentStock?.productAdjustmentItems?.$each?.[
          row.index
        ];
      if (rowValidation?.productUnit?.$reset) {
        rowValidation.productUnit.$reset();
      }
      if (rowValidation?.adjustmentAmount?.$reset) {
        rowValidation.adjustmentAmount.$reset();
      }
      if (rowValidation?.currentStockToAfterAdjustment?.$reset) {
        rowValidation.currentStockToAfterAdjustment.$reset();
      }
    },
    onProductUnitChange(row) {
      if (row.item.productUnit?.id) {
        const product = this.productList.find(
          p => p.productId === row.item.productNoAndName.id
        );

        const available = product?.productAvailableStock ?? 0;

        const conversionRate = this.conversionUnitRate.find(
          r => r.id === row.item.productUnit.id
        );

        if (conversionRate) {
          const availableAfterConversion = available * conversionRate.rate;
          row.item.productAvailableStock = availableAfterConversion;
          this.$set(row.item, "adjustmentAmount", 0);
          this.$set(
            row.item,
            "currentStockToAfterAdjustment",
            availableAfterConversion
          );
        }
      } else {
        row.item.productAvailableStock = undefined;
        this.$set(row.item, "adjustmentAmount", 0);
        row.item.currentStockToAfterAdjustment = undefined;
      }
    },
    isCurrentStockToAfterAdjustmentInvalid(row) {
      const rowV =
        this.$v.generalAdjustmentStock?.productAdjustmentItems?.$each?.[
          row.index
        ];
      return !!(
        rowV &&
        rowV.currentStockToAfterAdjustment &&
        rowV.currentStockToAfterAdjustment.$error
      );
    },
    createEmptyAdjustmentItem() {
      return {
        productNoAndName: "",
        adjustmentAmount: 0,
        productUnit: null,
        currentStockToAfterAdjustment: null,
      };
    },
    addAdjustmentItem() {
      this.generalAdjustmentStock.productAdjustmentItems.push(
        this.createEmptyAdjustmentItem()
      );
    },
    deleteAdjustmentItem(item, index) {
      this.generalAdjustmentStock.productAdjustmentItems.splice(index, 1);
    },
    convertUnitStock(row) {
      return row.item.productAvailableStock;
    },
    getProductOptions(currentIndex) {
      const selectedInOtherRows = new Set(
        this.generalAdjustmentStock.productAdjustmentItems
          .map((item, i) =>
            i !== currentIndex && item.productNoAndName?.id
              ? item.productNoAndName.id
              : null
          )
          .filter(Boolean)
      );
      return this.productOptions.filter(
        product => !selectedInOtherRows.has(product.id)
      );
    },
    resetFormOnStockTypeChange() {
      this.$v.$reset();
      this.submitAttempted = false;
      this.reasonStock = "";
      this.generalAdjustmentStock = {
        productAdjustmentItems: [this.createEmptyAdjustmentItem()],
      };
      this.returnProductAdjustmentStock = {
        GRNo: "",
        productAdjustmentItems: [],
      };
      this.physicalProductAdjustmentStock = {
        productAdjustmentItems: [],
      };
      this.getPhysicalProductAdjustmentStock();
    },
    onGRNoSelect(selected) {
      if (!selected) return;
      const grNo = selected.name;
      if (grNo) this.mapGRToProductListData(grNo);
    },
    mapGRToProductListData(GRNo) {
      const GR = this.allGRWithProductList.find(item => item.GRNo === GRNo);

      const productListData = GR.GRProduct.map(item => ({
        productId: item.product.productId,
        productNoAndName: `${item.product.productCode} - ${item.product.productNameMain}`,
        productAvailableStock: item.stockAvailable,
        productUnit: item.unit.unitNameTh,
        productUnitId: item.unit.unitId,
        productQtyOnGR: item.stockOnGR,
        adjustmentAmount: 0,
        currentStockToAfterAdjustment: item.stockAvailable,
      }));

      this.returnProductAdjustmentStock.productAdjustmentItems =
        productListData;
    },
    getPhysicalProductAdjustmentStock() {
      const productListData = this.productList.map(item => ({
        productId: item.productId,
        productNoAndName: `${item.productCode} - ${item.productNameMain}`,
        productAvailableStock: item.productAvailableStock,
        productUnit: item.productUnit.description,
        productUnitId: item.productUnit.id,
        adjustmentAmount: 0,
        currentStockToAfterAdjustment: item.productAvailableStock,
      }));
      this.physicalProductAdjustmentStock.productAdjustmentItems =
        productListData;
    },
  },
  watch: {
    stockType() {
      this.resetFormOnStockTypeChange();
    },
  },
};
</script>

<style scoped>
::v-deep .align-label .col-form-label {
  padding-top: 0px !important;
}

::v-deep .multiselect__tags {
  background-color: #ccecff6c;
  box-shadow: none;
  /* border-left: none !important; */
  overflow: hidden;
}

::v-deep .multiselect__single,
::v-deep .multiselect__input,
::v-deep .multiselect__placeholder {
  background-color: transparent !important;
  /* border-left: none !important; */
  overflow: hidden;
}

::v-deep span.multiselect__placeholder {
  margin-left: 6px !important;
}

::v-deep .multiselect__input,
::v-deep .multiselect__single {
  padding-left: 6px;
  /* border-left: none !important; */
  overflow: hidden;
}

::v-deep span.multiselect__single {
  margin-left: 6px !important;
  /* border-left: none !important; */
  overflow: hidden;
}

::v-deep .multiselect__single {
  margin-bottom: 0;
  padding-left: 0;
  /* border-left: none !important; */
  overflow: hidden;
}

::v-deep .multiselect__tags {
  box-shadow: none;
}

::v-deep .multiselect--disabled {
  background-color: #ebebeb !important;
  color: #c6c6c6 !important;
  opacity: 1 !important;
}

::v-deep .multiselect--disabled .multiselect__tags {
  background-color: #ebebeb !important;
  cursor: not-allowed;
  overflow: hidden;
  color: #c6c6c6 !important;
}

::v-deep .multiselect--disabled .multiselect__placeholder {
  background-color: #ebebeb !important;
  color: #c6c6c6 !important;
}

::v-deep .multiselect--disabled .multiselect__select {
  background-color: #ebebeb !important;
  cursor: not-allowed;
  border: 1px solid #ced4da !important;
  border-left: none !important;
  overflow: hidden;

  border-radius: 4px;
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}

::v-deep .multiselect__element .multiselect__option {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

::v-deep #GeneralAdjustmentStockTable tbody tr td {
  border-top: none !important;
  border-bottom: 1px solid #dee2e6 !important;
}

::v-deep #stockAdjustmentModal .modal-body {
  padding-bottom: 0 !important;
}

::v-deep #GeneralAdjustmentStockTable tbody tr:last-child td {
  border-bottom: none !important;
}

::v-deep #ReturnProductAdjustmentStockTable tbody tr td {
  border-top: none !important;
  border-bottom: 1px solid #dee2e6 !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

::v-deep #ReturnProductAdjustmentStockTable tbody tr:last-child td {
  border-bottom: none !important;
}

::v-deep .bottom-add-row {
  text-align: left !important;
  margin-top: 10px;
}

::v-deep #GeneralAdjustmentStockTable.table-hover tbody tr:last-child:hover {
  background-color: transparent !important;
}

::v-deep #GeneralAdjustmentStockTable thead tr th:nth-child(1),
::v-deep
  #GeneralAdjustmentStockTable
  tbody
  tr:not(:last-child)
  td:nth-child(1) {
  text-align: center !important;
  vertical-align: middle !important;
}

::v-deep #ReturnProductAdjustmentStockTable thead tr th,
::v-deep #ReturnProductAdjustmentStockTable tbody tr td {
  text-align: left !important;
  vertical-align: middle !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

::v-deep #GeneralAdjustmentStockTable .bottom-row-cell {
  text-align: left !important;
}

::v-deep .multiselect-unit .multiselect__tags {
  overflow: visible;
  min-width: 0;
}

::v-deep .multiselect-unit .multiselect__single,
::v-deep .multiselect-unit span.multiselect__single {
  white-space: normal !important;
  word-break: break-word;
  overflow-wrap: break-word;
  overflow: visible !important;
}

::v-deep #GeneralAdjustmentStockTable thead tr th,
::v-deep #GeneralAdjustmentStockTable tbody tr td {
  vertical-align: middle !important;
}

::v-deep #ReturnProductAdjustmentStockTable td.text-wrap-cell {
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

::v-deep #PhysicalProductAdjustmentStockTable thead tr th,
::v-deep #PhysicalProductAdjustmentStockTable tbody tr td {
  vertical-align: middle !important;
}

::v-deep #PhysicalProductAdjustmentStockTable td.text-wrap-cell {
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

::v-deep .delete-btn {
  background-color: transparent;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  color: #a3a4a6;
  font-size: 20px;
  width: 48px;
  height: 38px;
}

.delete-btn:hover:not(:disabled) {
  background-color: #d33;
  color: #fff;
}

.delete-btn:disabled {
  border-color: #ebebeb !important;
  color: #ebebeb !important;
  cursor: not-allowed;
}
</style>
